import { useUserStore } from '@/stores/user';
import { computed, reactive } from 'vue';

const PORTAL_ALLOWED_ORG_IDS: string[] = ['H3F'];
const REVIEW_QUEUE_ALLOWED_ORG_IDS: string[] = ['H3F'];

/**
 * Composable for checking user permissions
 * Uses reactive patterns to efficiently cache permission results
 */
export function usePermissions() {
    const userStore = useUserStore();

    const isPortalOrg = computed(() =>
        PORTAL_ALLOWED_ORG_IDS.includes(userStore.userData?.object_id ?? '')
    );

    const isReviewQueueOrg = computed(() =>
        REVIEW_QUEUE_ALLOWED_ORG_IDS.includes(userStore.userData?.object_id ?? '')
    );

    // Cache permission results in reactive object
    const permissions = reactive({
        article: {
            // Knowledge article permissions
            view: computed(() => userStore.hasPermission('kb_view')),
            add: computed(() => userStore.hasPermission('kb_add')),
            edit: computed(() => userStore.hasPermission('kb_edit')),
            delete: computed(() => userStore.hasPermission('kb_del')),
            publish: computed(() => userStore.hasPermission('kb_publish')),
            template: computed(() => userStore.hasPermission('manage_templates')),
        },
        dashboard: {
            // Dashboard/Analytics permissions
            view: computed(() => userStore.hasPermission('dashboards_view')),
            manage: computed(() => userStore.hasPermission('manage_dashboards')),
        },
        reports: {
            // Reports permissions
            view: computed(() => userStore.hasPermission('report_view')),
            manage: computed(() => userStore.hasPermission('manage_reports')),
        },
        customer: {
            // Customer permissions
            view: computed(() => userStore.hasPermission('cust_view')),
            add: computed(() => userStore.hasPermission('cust_add')),
            edit: computed(() => userStore.hasPermission('cust_edit')),
            delete: computed(() => userStore.hasPermission('cust_del')),
            merge: computed(() => userStore.hasPermission('cust_merge')),
        },
        contact: {
            // Contact (Customer User) permissions
            add: computed(() => userStore.hasPermission('cust_user_add')),
            edit: computed(() => userStore.hasPermission('cust_user_edit')),
            delete: computed(() => userStore.hasPermission('cust_user_del')),
        },
        location: {
            // Customer Location permissions
            edit: computed(() => userStore.hasPermission('cust_loc_edit')),
        },
        product: {
            // Customer Product permissions
            manage: computed(() => userStore.hasPermission('cust_tech_manage')),
        },
        bot: {
            // Bot/AI Studio permissions
            view: computed(() => userStore.hasPermission('bot_view')),
            add: computed(() => userStore.hasPermission('bot_add')),
            edit: computed(() => userStore.hasPermission('bot_edit')),
            delete: computed(() => userStore.hasPermission('bot_del')),
            stats: computed(() => userStore.hasPermission('bot_stats')),
            train: computed(() => userStore.hasPermission('bot_contrib')),
        },
        journey: {
            // Journey permissions
            view: computed(() => userStore.hasPermission('journey_view')),
            manage: computed(() => userStore.hasPermission('journey_manage')),
            addToCases: computed(() => userStore.hasPermission('journey_cases')),
            overrideActions: computed(() => userStore.hasPermission('journey_case_stages')),
        },
        sharedView: {
            // Shared View permissions (Inbox)
            view: computed(() => userStore.hasPermission('rv_view')),
            add: computed(() => userStore.hasPermission('rv_add')),
            edit: computed(() => userStore.hasPermission('rv_edit')),
            delete: computed(() => userStore.hasPermission('rv_del')),
        },
        task: {
            // Task permissions
            view: computed(() => userStore.hasPermission('tasks_view')),
            add: computed(() => userStore.hasPermission('tasks_add')),
            edit: computed(() => userStore.hasPermission('tasks_edit')),
            delete: computed(() => userStore.hasPermission('tasks_del')),
            reopen: computed(() => userStore.hasPermission('tasks_reopen')),
            undelete: computed(() => userStore.hasPermission('tasks_undelete')),
        },
        issue: {
            // Case/Issue permissions
            view: computed(() => userStore.hasPermission('issue_view')),
            add: computed(() => userStore.hasPermission('issue_add')),
            edit: computed(() => userStore.hasPermission('issue_edit')),
            delete: computed(() => userStore.hasPermission('issue_del')),
            merge: computed(() => userStore.hasPermission('issue_merge')),
            editAll: computed(() => userStore.hasPermission('issue_edit_all')),
        },
        profile: {
            // Profile permissions
            edit: computed(() => userStore.hasPermission('profile_edit')),
            avatar: computed(() => userStore.hasPermission('profile_avatar')),
            notifications: computed(() => userStore.hasPermission('profile_notif')),
        },
        organization: {
            // Organization Settings permissions
            edit: computed(() => userStore.hasPermission('org_edit')),
            manageTeams: computed(() => userStore.hasPermission('team_edit')),
            manageUsers: computed(() => userStore.hasPermission('user_edit')),
            manageUserPasswords: computed(() => userStore.hasPermission('user_pw')),
        },
        roles: {
            // Roles & Permissions permissions
            view: computed(() => userStore.hasPermission('role_view')),
            add: computed(() => userStore.hasPermission('role_add')),
            edit: computed(() => userStore.hasPermission('role_edit')),
            delete: computed(() => userStore.hasPermission('role_del')),
        },
        administration: {
            // Administration Settings permissions
            manageAutomations: computed(() => userStore.hasPermission('manage_automations')),
            manageChannels: computed(() => userStore.hasPermission('manage_channels')),
            manageIntegrations: computed(() => userStore.hasPermission('manage_integrations')),
            // Same permission as manageIntegrations until a dedicated actions permission exists
            manageActions: computed(() => userStore.hasPermission('manage_integrations')),
            manageWorkflows: computed(() => userStore.hasPermission('manage_workflows')),
            manageFields: computed(() => userStore.hasPermission('manage_fields')),
            manageTags: computed(() => userStore.hasPermission('manage_tags')),
            manageQuickReplies: computed(() => userStore.hasPermission('manage_responses')),
            manageEmailTemplates: computed(() => userStore.hasPermission('manage_email_templates')),
            manageApiSettings: computed(() => userStore.hasPermission('manage_api_settings')),
            manageTokens: computed(() => userStore.hasPermission('manage_tokens')),
            manageCaseSubmissionForms: computed(() => userStore.hasPermission('manage_cl_issue')),
            manageScopeOfWorkForms: computed(() => userStore.hasPermission('manage_cl_sow')),
            manageCloseoutForms: computed(() => userStore.hasPermission('manage_cl_closeout')),
        },
        knowledge: {
            // Knowledge Base Management permissions
            createLibrary: computed(() => userStore.hasPermission('kb_root_add')),
            editLibrarySettings: computed(() => userStore.hasPermission('kb_root_edit')),
        },
        technician: {
            // Field Services - Technician permissions
            dispatch: computed(() => userStore.hasPermission('tech_dispatch')),
            view: computed(() => userStore.hasPermission('tech_view')),
            add: computed(() => userStore.hasPermission('tech_add')),
            edit: computed(() => userStore.hasPermission('tech_edit')),
            delete: computed(() => userStore.hasPermission('tech_del')),
            addUser: computed(() => userStore.hasPermission('tech_user_add')),
            editUser: computed(() => userStore.hasPermission('tech_user_edit')),
            deleteUser: computed(() => userStore.hasPermission('tech_user_del')),
            manageUserPasswords: computed(() => userStore.hasPermission('tech_user_pw_edit')),
        },
        billing: {
            viewPayables: computed(() => userStore.hasPermission('billing_payables')),
            viewReceivables: computed(() => userStore.hasPermission('billing_receivables')),
            viewBilling: computed(() => userStore.hasPermission('billing_view')),
            issueEstimate: computed(() => userStore.hasPermission('issue_estimate')),
        },
        portal: {
            view: computed(() => isPortalOrg.value && userStore.hasPermission('portal_view')),
            add: computed(() => isPortalOrg.value && userStore.hasPermission('portal_add')),
            edit: computed(() => isPortalOrg.value && userStore.hasPermission('portal_edit')),
            delete: computed(() => isPortalOrg.value && userStore.hasPermission('portal_del')),
        },
    });

    // Helper methods for more semantic usage
    const can = {
        // Article permissions with more readable function names
        viewArticle: () => permissions.article.view,
        addArticle: () => permissions.article.add,
        editArticle: () => permissions.article.edit,
        deleteArticle: () => permissions.article.delete,
        publishArticle: () => permissions.article.publish,
        manageTemplates: () => permissions.article.template,

        // Article permissions with more readable function names
        viewForm: () => true,
        addForm: () => true,
        editForm: () => true,
        deleteForm: () => true,

        // Dashboard permissions
        viewDashboards: () => permissions.dashboard.view,
        manageDashboards: () => permissions.dashboard.manage,

        // Reports permissions
        viewReports: () => permissions.reports.view,
        manageReports: () => permissions.reports.manage,

        // Customer permissions
        viewCustomers: () => permissions.customer.view,
        addCustomers: () => permissions.customer.add,
        editCustomers: () => permissions.customer.edit,
        deleteCustomers: () => permissions.customer.delete,
        mergeCustomers: () => permissions.customer.merge,

        // Contact (Customer User) permissions
        addContacts: () => permissions.contact.add,
        editContacts: () => permissions.contact.edit,
        deleteContacts: () => permissions.contact.delete,

        // Location permissions
        editLocations: () => permissions.location.edit,

        // Product permissions
        manageProducts: () => permissions.product.manage,

        // Bot/AI Studio permissions
        viewAIStudio: () => permissions.bot.view,
        viewReviewQueue: () => isReviewQueueOrg.value && permissions.bot.view,
        addAgent: () => permissions.bot.add,
        editAgent: () => permissions.bot.edit,
        deleteAgent: () => permissions.bot.delete,
        viewPerformance: () => permissions.bot.stats,
        trainAgent: () => permissions.bot.train,

        // Journey permissions
        viewJourneys: () => permissions.journey.view,
        manageJourneys: () => permissions.journey.manage,
        addJourneysToCases: () => permissions.journey.addToCases,
        overrideJourneyActions: () => permissions.journey.overrideActions,

        // Shared View permissions
        viewSharedViews: () => permissions.sharedView.view,
        addSharedViews: () => permissions.sharedView.add,
        editSharedViews: () => permissions.sharedView.edit,
        deleteSharedViews: () => permissions.sharedView.delete,

        // Task permissions
        viewTasks: () => permissions.task.view,
        addTasks: () => permissions.task.add,
        editTasks: () => permissions.task.edit,
        deleteTasks: () => permissions.task.delete,
        reopenTasks: () => permissions.task.reopen,
        undeleteTasks: () => permissions.task.undelete,

        // Case/Issue permissions
        viewCases: () => permissions.issue.view,
        addCases: () => permissions.issue.add,
        editCases: () => permissions.issue.edit,
        deleteCases: () => permissions.issue.delete,
        mergeCases: () => permissions.issue.merge,
        editAllCases: () => permissions.issue.editAll,

        // Profile permissions
        editMyProfile: () => permissions.profile.edit,
        editMyAvatar: () => permissions.profile.avatar,
        manageMyNotifications: () => permissions.profile.notifications,

        // Organization Settings permissions
        editOrganizationSettings: () => permissions.organization.edit,
        manageTeams: () => permissions.organization.manageTeams,
        manageUsers: () => permissions.organization.manageUsers,
        manageUserPasswords: () => permissions.organization.manageUserPasswords,

        // Roles & Permissions permissions
        viewRolesPermissions: () => permissions.roles.view,
        addRolesPermissions: () => permissions.roles.add,
        editRolesPermissions: () => permissions.roles.edit,
        deleteRolesPermissions: () => permissions.roles.delete,

        // Administration Settings permissions
        manageAutomations: () => permissions.administration.manageAutomations,
        manageChannels: () => permissions.administration.manageChannels,
        manageIntegrations: () => permissions.administration.manageIntegrations,
        manageActions: () => permissions.administration.manageActions,
        manageWorkflows: () => permissions.administration.manageWorkflows,
        manageFields: () => permissions.administration.manageFields,
        manageTags: () => permissions.administration.manageTags,
        manageQuickReplies: () => permissions.administration.manageQuickReplies,
        manageEmailTemplates: () => permissions.administration.manageEmailTemplates,
        manageApiSettings: () => permissions.administration.manageApiSettings,
        manageTokens: () => permissions.administration.manageTokens,
        manageCaseSubmissionForms: () => permissions.administration.manageCaseSubmissionForms,
        manageScopeOfWorkForms: () => permissions.administration.manageScopeOfWorkForms,
        manageCloseoutForms: () => permissions.administration.manageCloseoutForms,

        // Knowledge Base Management permissions
        createKnowledgeLibrary: () => permissions.knowledge.createLibrary,
        editKnowledgeLibrarySettings: () => permissions.knowledge.editLibrarySettings,

        // Field Services - Technician permissions
        dispatchTechnician: () => permissions.technician.dispatch,
        viewFieldServices: () => permissions.technician.view,
        addTechnician: () => permissions.technician.add,
        editTechnician: () => permissions.technician.edit,
        deleteTechnician: () => permissions.technician.delete,
        addTechnicianUser: () => permissions.technician.addUser,
        editTechnicianUser: () => permissions.technician.editUser,
        deleteTechnicianUser: () => permissions.technician.deleteUser,
        manageTechnicianUserPasswords: () => permissions.technician.manageUserPasswords,

        // Billing permissions
        viewBillingPayables: () => permissions.billing.viewPayables,
        viewBillingReceivables: () => permissions.billing.viewReceivables,
        viewBilling: () => permissions.billing.viewBilling,
        viewIssueEstimate: () => permissions.billing.issueEstimate,

        // Portal permissions (H3F org-gated)
        viewPortals: () => permissions.portal.view,
        addPortal: () => permissions.portal.add,
        editPortal: () => permissions.portal.edit,
        deletePortal: () => permissions.portal.delete,
    };

    return {
        // Return both the raw permissions object and the semantic helper methods
        permissions,
        can,
    };
}
